/**
 * The dated roadmap's data (D193 phase 2B): the runs of the last 90 days, grounded with the rows
 * into the Gantt the view draws. The workbench's `useRoadmapGantt.ts`, with its run source swapped
 * for the console's (../timeline/useWorkTimeline.ts: the project's runs route) and the Picture
 * menu's look left out (the console draws one look). The viewer's choices (the switch, the lanes)
 * are remembered per viewer and level, as the workbench remembers them.
 *
 * Console: one answer holds every repository's runs, so a repository's Gantt counts only the
 * roadmap links that name that repository (on the desktop the repository's own host answers).
 */
import { useCallback, useMemo, useState } from 'react';

import { useWorkTimeline } from '../timeline/useWorkTimeline.ts';
import type { RoadmapRowLike } from './roadmapGantt.ts';
import { type GanttLanes, ganttRunsRange, groundForView, readGanttLanes, readTimelineMode, type TimelineMode } from './roadmapGanttModel.ts';

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* a per-viewer convenience only */
  }
}

/** The switch and the lanes, remembered per viewer and level. */
export function useGanttChoices(level: 'repository' | 'project') {
  const [mode, setModeState] = useState<TimelineMode>(() => readTimelineMode(stored(`roadmap.${level}.timelineMode`)));
  const [lanes, setLanesState] = useState<GanttLanes>(() => readGanttLanes(stored(`roadmap.${level}.ganttLanes`)));
  // Stable, so an effect can switch the mode without running again.
  const setMode = useCallback(
    (next: TimelineMode) => {
      setModeState(next);
      remember(`roadmap.${level}.timelineMode`, next);
    },
    [level],
  );
  return {
    mode,
    setMode,
    lanes,
    setLanes: (next: GanttLanes) => {
      setLanesState(next);
      remember(`roadmap.${level}.ganttLanes`, next);
    },
  };
}

/** The Gantt for these rows: undefined until the runs have been read (so it never flashes "no pace"). */
export function useRoadmapGantt(rows: readonly RoadmapRowLike[], opts: { level: 'repository' | 'project'; projectId?: string; rootId?: string; lanes: GanttLanes; enabled: boolean }) {
  // The range is set when the view opens; the timeline's own poll keeps the runs fresh within it.
  const [range] = useState(() => ganttRunsRange(Date.now()));
  const data = useWorkTimeline({ scope: 'project', projectId: opts.projectId, from: range.from, to: range.to, enabled: opts.enabled, pollMs: 60_000 });
  const ready = opts.enabled && !data.loading;
  const runs = useMemo(() => (opts.level === 'repository' ? data.runs.map((r) => ({ ...r, roadmap: (r.roadmap ?? []).filter((ref) => ref.rootId === opts.rootId) })) : data.runs), [data.runs, opts.level, opts.rootId]);
  const grounding = useMemo(() => (ready ? groundForView(rows, runs, { now: data.at, level: opts.level, lanes: opts.lanes }) : undefined), [ready, rows, runs, data.at, opts.level, opts.lanes]);
  return { grounding, loading: opts.enabled && data.loading, error: data.error, runs: data.runs };
}
